import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import { prefetchIdleRoutes } from "./utils/prefetchRoute";
import { LazyMotion } from "framer-motion";
import { useLenis } from "./hooks/useLenis";

const loadFramerFeatures = () => import("@/utils/framerFeatures").then(res => res.default);

// Lazy-load non-critical UI that isn't needed for first paint
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy-loaded routes
const TermosDeUso = lazy(() => import("./pages/TermosDeUso"));
const PoliticaDePrivacidade = lazy(() => import("./pages/PoliticaDePrivacidade"));
const Obrigado = lazy(() => import("./pages/Obrigado"));
const NotFound = lazy(() => import("./pages/NotFound"));

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Deferred ScrollToTop — loaded after first paint */
const DeferredScrollToTop = lazy(() =>
  import("./components/ScrollToTop").then(m => ({ default: m.ScrollToTop }))
);

const CookieConsent = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieConsent })));

const AppContent = () => {
  useLenis();

  useEffect(() => {
    // Delay prefetching to avoid contention with initial hydration
    const timer = setTimeout(() => {
      prefetchIdleRoutes();
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <LazyMotion features={loadFramerFeatures}>
      <div aria-hidden="true" className="fixed inset-0 z-[9990] pointer-events-none noise-overlay" />
      <BrowserRouter>
        <Suspense fallback={null}>
          <DeferredScrollToTop />
          <Toaster />
          <Sonner />
          <CookieConsent />
        </Suspense>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/termos-de-uso" element={<TermosDeUso />} />
            <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
            <Route path="/obrigado" element={<Obrigado />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </LazyMotion>
  );
};

const App = () => (
    <AppContent />
);

export default App;
